import { reactive } from 'vue';
import { CHAT_UNREAD_POLL_MS, ChatLastMessage, fetchChatUnread } from '@/utils/chat';
import { onChatEvent, useChatRealtime } from '@/utils/chat_realtime';

/**
 * What the signed-in side has waiting in the support chat — the badge on
 * Chat in the admin's navs, the count on the apps' chat button — kept
 * current: asked of the API when it starts, every CHAT_UNREAD_POLL_MS while
 * the tab is in view, when the tab comes back into view, and at once when a
 * live notice says something changed.
 *
 * Started once someone is signed in (the admin's App.vue, the apps' chat
 * button), stopped when they sign out. It holds the live connection
 * (utils/chat_realtime) for as long as it runs. The same module lives in the
 * admin and both apps (see utils/chat).
 */

export interface ChatUnreadLatest {
  id: string;
  // The conversation it is in, and what that is called.
  conversationId: string;
  title: string;
  preview: string;
  kind: ChatLastMessage['kind'];
  authorName: string;
  createdAt: string;
}

export const chatUnread = reactive({
  side: null as null | 'staff' | 'organization',
  /** Conversations with unread messages. */
  conversations: 0,
  /** Unread messages. */
  messages: 0,
  /** The newest unread message (an organization's side). */
  latest: null as ChatUnreadLatest | null,
  loadedAt: 0,
});

let canAsk: (() => boolean) | null = null;
let timer: number | null = null;
let soon: number | null = null;
let releaseRealtime: (() => void) | null = null;
let stopListening: (() => void) | null = null;
let asking = false;

const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

/** Asks now — after something that changes what is unread. */
export async function refreshChatUnread(): Promise<void> {
  if (!canAsk || !canAsk() || !visible() || asking) return;
  asking = true;
  try {
    const result = await fetchChatUnread();
    if (result.ok && result.data && canAsk) {
      chatUnread.side = result.data.side || null;
      chatUnread.conversations = Number(result.data.conversations) || 0;
      chatUnread.messages = Number(result.data.messages) || 0;
      chatUnread.latest = result.data.latest || null;
      chatUnread.loadedAt = Date.now();
    }
  } finally {
    asking = false;
  }
}

/** Asks shortly — several notices in a row are one request. */
function refreshSoon(): void {
  if (soon !== null) window.clearTimeout(soon);
  soon = window.setTimeout(() => {
    soon = null;
    refreshChatUnread();
  }, 400);
}

/**
 * The signed-in side has read a conversation: its newest unread message, if
 * that was in it, stops peeking at once; the counts follow from the API.
 */
export function noteChatRead(conversationId?: string | null): void {
  if (chatUnread.latest && (!conversationId || chatUnread.latest.conversationId === conversationId)) {
    chatUnread.latest = null;
  }
  refreshSoon();
}

const onVisibilityChange = () => {
  if (visible()) refreshChatUnread();
};

/**
 * Starts keeping the count. `isSignedIn` says whether there is someone to ask
 * for; `getToken` gives the session's token for the live connection.
 */
export function startChatNotifications(isSignedIn: () => boolean, getToken: () => string | null): void {
  if (canAsk) return;
  canAsk = isSignedIn;
  releaseRealtime = useChatRealtime(getToken);
  stopListening = onChatEvent((event) => {
    if (event.type !== 'typing') refreshSoon();
  });
  refreshChatUnread();
  timer = window.setInterval(refreshChatUnread, CHAT_UNREAD_POLL_MS);
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('focus', refreshChatUnread);
}

export function stopChatNotifications(): void {
  if (!canAsk) return;
  if (timer !== null) window.clearInterval(timer);
  if (soon !== null) window.clearTimeout(soon);
  timer = null;
  soon = null;
  document.removeEventListener('visibilitychange', onVisibilityChange);
  window.removeEventListener('focus', refreshChatUnread);
  if (stopListening) stopListening();
  if (releaseRealtime) releaseRealtime();
  stopListening = null;
  releaseRealtime = null;
  canAsk = null;
  chatUnread.side = null;
  chatUnread.conversations = 0;
  chatUnread.messages = 0;
  chatUnread.latest = null;
  chatUnread.loadedAt = 0;
}
